/**
 * Lab Net Exposure PT ladder request helpers (pure — unit-tested).
 * `useFancyPtLadder` calls these then hits getLadder / getLadderTotal.
 */

import type { BookType } from '@/features/net-exposure/fixture'
import type { GetLadderRequest } from '@/api/map/workstation'

export type FancyPtLadderRoute = 'getLadder' | 'getLadderTotal'

/** Lab: Total Book + non-player → getLadderTotal (player lt/gt flipped). */
export function fancyPtLadderUsesTotal(
  selectedType: BookType,
  nodeLevel: 'agent' | 'player',
): boolean {
  return selectedType === 'Total Book' && nodeLevel !== 'player'
}

export function fancyPtLadderRoute(
  selectedType: BookType,
  nodeLevel: 'agent' | 'player',
): FancyPtLadderRoute {
  return fancyPtLadderUsesTotal(selectedType, nodeLevel) ? 'getLadderTotal' : 'getLadder'
}

/** Lab ladder `type` for Betfair line session (`lineLadder`) markets. */
export const BETFAIR_LINE_SESSION_LADDER_TYPE = 'betfair-line-sessions'

/**
 * Lab `Book/getLadder` body: `{ sessionId, userId, level, type? }`.
 * `level` prefers tree `accessLevel` (ROLE_LABEL); falls back by node seat.
 * Betfair line session → `type: betfair-line-sessions` (Lab workstation.js).
 */
export function buildFancyPtLadderBody(input: {
  marketId: string
  userId: string
  accessLevel?: string
  selectedType: BookType
  nodeLevel: 'agent' | 'player'
  /** CM root fallback is Company Master; agent console uses Master. */
  agentFallbackLevel?: string
  /** When true, Lab sends type betfair-line-sessions (not custom fancy). */
  lineSession?: boolean
}): GetLadderRequest {
  const agentFallback = input.agentFallbackLevel ?? 'Master'
  return {
    sessionId: input.marketId,
    userId: input.userId,
    level:
      input.accessLevel ||
      (input.nodeLevel === 'player' ? 'Member' : agentFallback),
    selectedType: input.selectedType,
    ...(input.lineSession ? { type: BETFAIR_LINE_SESSION_LADDER_TYPE } : {}),
  }
}
